/**
 * Demo Express script — pure data.
 * Each scene is rendered by DemoSceneRenderer.jsx and lasts ~`duration` ms.
 * The player advances automatically; some scenes expose a CTA button that
 * can fast-forward when the user clicks (auto-advance still wins if idle).
 *
 * Total runtime target : ~120 s (≤ 2 min, dynamic).
 *
 * Mock state schema (drives the persistent stat-bar):
 *   { day, treasury, drivers, vehicles, contracts, reputation, achievements }
 *
 * i18n note (29/05/2026): scene `narrator`, `cta.label` and any visual labels
 * are now translation KEYS (resolved by DemoSceneRenderer / Demo via the
 * useTranslation hook). The values themselves live in i18n/translations.js
 * under the demo_scene_* namespace.
 */

export const DEMO_INITIAL_STATE = {
  day: 1,
  treasury: 50000,
  drivers: 0,
  vehicles: 1,
  contracts: 0,
  reputation: 50,
  achievements: 0,
};

export const DEMO_SCENES = [
  {
    id: 'intro',
    kind: 'intro',
    duration: 5500,
    narratorKey: 'demo_scene_intro_narrator',
    cta: { labelKey: 'demo_scene_intro_cta', advance: true },
  },
  {
    id: 'hire-driver',
    kind: 'hire',
    duration: 12000,
    narratorKey: 'demo_scene_hire_narrator',
    visual: {
      driver: {
        name: 'Sophie Martin',
        experienceKey: 'demo_scene_hire_experience',
        specialtyKey: 'demo_scene_hire_specialty',
        rating: 4,
        cost: 2400,
      },
    },
    stateUpdate: { drivers: 1, treasury: 47600 },
    cta: { labelKey: 'demo_scene_hire_cta', advance: true },
  },
  {
    id: 'accept-contract',
    kind: 'contract',
    duration: 12000,
    narratorKey: 'demo_scene_contract_narrator',
    visual: {
      contract: {
        client: 'Carrefour Logistique',
        days: 28,
        dailyRate: 850,
        totalRevenue: 23800,
        kindKey: 'demo_scene_contract_kind',
        kmTotal: 1240,
      },
    },
    stateUpdate: { contracts: 1, reputation: 55 },
    cta: { labelKey: 'demo_scene_contract_cta', advance: true },
  },
  {
    id: 'fast-forward',
    kind: 'fastForward',
    duration: 14000,
    narratorKey: 'demo_scene_ff_narrator',
    visual: { daysToAdvance: 3, dailyNet: 620 },
    stateUpdate: { day: 4, treasury: 49460 },
    cta: { labelKey: 'demo_scene_ff_cta', advance: true },
  },
  {
    id: 'event-breakdown',
    kind: 'event',
    duration: 12500,
    narratorKey: 'demo_scene_event_narrator',
    visual: {
      event: {
        titleKey: 'demo_scene_event_title',
        severity: 'warn',
        cost: 1200,
        impactKey: 'demo_scene_event_impact',
      },
    },
    stateUpdate: { treasury: 48260 },
    cta: { labelKey: 'demo_scene_event_cta', advance: true },
  },
  {
    id: 'capex-investment',
    kind: 'capex',
    duration: 14000,
    narratorKey: 'demo_scene_capex_narrator',
    visual: {
      capex: {
        vehicleKey: 'demo_scene_capex_vehicle',
        price: 45000,
        annualISsaved: 1240,
        totalISsaved: 6200,
        netCost: 38800,
      },
    },
    stateUpdate: { vehicles: 2, treasury: 3260 },
    cta: { labelKey: 'demo_scene_capex_cta', advance: true },
  },
  {
    id: 'hire-support',
    kind: 'support',
    duration: 11000,
    narratorKey: 'demo_scene_support_narrator',
    visual: {
      support: {
        name: 'Nadia Cheikh',
        roleKey: 'demo_scene_support_role',
        unlockKeys: ['demo_scene_support_u1', 'demo_scene_support_u2', 'demo_scene_support_u3'],
        monthlyCost: 3200,
      },
    },
    stateUpdate: { reputation: 60 },
    cta: { labelKey: 'demo_scene_support_cta', advance: true },
  },
  {
    id: 'achievement',
    kind: 'achievement',
    duration: 9000,
    narratorKey: 'demo_scene_ach_narrator',
    visual: {
      achievement: {
        id: 'first-steps',
        tier: 'gold',
        titleKey: 'demo_scene_ach_title',
        descriptionKey: 'demo_scene_ach_description',
        xp: 250,
      },
    },
    stateUpdate: { achievements: 1 },
    cta: { labelKey: 'demo_scene_ach_cta', advance: true },
  },
  {
    id: 'recap',
    kind: 'recap',
    duration: 16000,
    narratorKey: 'demo_scene_recap_narrator',
    visual: {
      featureKeys: [
        'demo_scene_recap_f1', 'demo_scene_recap_f2', 'demo_scene_recap_f3', 'demo_scene_recap_f4',
        'demo_scene_recap_f5', 'demo_scene_recap_f6', 'demo_scene_recap_f7', 'demo_scene_recap_f8',
      ],
    },
    cta: null,
  },
  {
    id: 'contact',
    kind: 'contact',
    duration: 0,
    narratorKey: 'demo_scene_contact_narrator',
    cta: null,
  },
];

export const DEMO_TOTAL_DURATION_MS = DEMO_SCENES.reduce((s, sc) => s + (sc.duration || 0), 0);
